import { axiosClient } from "../AxiosClient";
import {
  parseWhatsappFlowPayload,
  prepareStepConnectionPayload,
} from "./whatsappFlowStorage";

/** Ruta relativa de step-connections; URL completa = `axiosClient.defaults.baseURL` + este path (p. ej. API principal + `/v1/step-connections`). */
export const STEP_CONNECTIONS_PATH = "/v1/step-connections";

const LIST_PATH = STEP_CONNECTIONS_PATH;

/**
 * Obtiene el identificador del recurso creado desde distintas formas habituales de respuesta REST.
 * @param {unknown} data
 * @param {import("axios").AxiosResponse} response
 * @returns {string|null}
 */
const extractCreatedResourceId = (data, response) => {
  if (data && typeof data === "object") {
    if (data.uuid != null) return String(data.uuid);
    if (data.id != null) return String(data.id);
    const nested = data.data;
    if (nested && typeof nested === "object") {
      if (nested.uuid != null) return String(nested.uuid);
      if (nested.id != null) return String(nested.id);
    }
  }
  const loc = response?.headers?.location || response?.headers?.Location;
  if (loc && typeof loc === "string") {
    const trimmed = loc.split("?")[0];
    const seg = trimmed.split("/").filter(Boolean).pop();
    if (seg) return seg;
  }
  return null;
};

/**
 * @typedef {Object} StepConnectionListItem
 * @property {string} uuid
 * @property {string} [createdDate]
 * @property {string} [lastModifiedDate]
 * @property {string|null} [name]
 * @property {string|null} [description]
 * @property {"PRIMARY"|"SECONDARY"} [flowRole]
 */

/**
 * @returns {Promise<StepConnectionListItem[]>}
 */
export const listStepConnections = async () => {
  const { data } = await axiosClient.get(LIST_PATH);
  return Array.isArray(data) ? data : [];
};

/**
 * GET `/v1/step-connections/{uuid}` — obtiene un flujo y lo convierte al estado interno del editor.
 *
 * @param {string} uuid
 * @returns {Promise<{ uuid: string, name: string, description: string, flowRole: string, nodes: object[], edges: object[] }>}
 */
export const getStepConnectionByUuid = async (uuid) => {
  const { data } = await axiosClient.get(`${LIST_PATH}/${uuid}`);
  const parsed = parseWhatsappFlowPayload(data);
  return {
    uuid: data?.uuid ?? uuid,
    ...parsed,
  };
};

/**
 * POST `/v1/step-connections` — crea un flujo (step connection).
 * - Cuerpo: objeto con `version`, `name`, `description`, `nodes`, `edges`, `steps` (ver `buildWhatsappFlowPayload` + opcional `prepareStepConnectionPayload`).
 * - Auth: la misma que `axiosClient` (Bearer en `AxiosClient.js`).
 * - Respuesta esperada: JSON con `uuid` o `id`, o cabecera `Location` con el recurso creado. Códigos 2xx tratados como éxito.
 *
 * @param {object} payload — salida de `prepareStepConnectionPayload(buildWhatsappFlowPayload(...))` o equivalente
 * @returns {Promise<{ uuid: string }>}
 */
export const createStepConnection = async (payload) => {
  const body = prepareStepConnectionPayload(payload);
  const response = await axiosClient.post(LIST_PATH, body);
  const uuid = extractCreatedResourceId(response.data, response);
  if (!uuid) {
    throw new Error("La API no devolvió uuid ni id al crear el flujo.");
  }
  return { uuid };
};

/**
 * @param {string} uuid
 * @param {object} payload
 * @returns {Promise<void>}
 */
export const updateStepConnection = async (uuid, payload) => {
  const body = prepareStepConnectionPayload(payload);
  await axiosClient.put(`${LIST_PATH}/${uuid}`, body);
};

/**
 * GET `/v1/public/house-types` — listado público de tipos de vivienda (orden de presentación).
 * @returns {Promise<string[]>}
 */
export const getPublicHouseTypes = async () => {
  const { data } = await axiosClient.get("/v1/public/house-types");
  return Array.isArray(data) ? data.filter((item) => typeof item === "string") : [];
};

const filterValueLabelItems = (data) =>
  Array.isArray(data)
    ? data.filter(
        (item) =>
          item &&
          typeof item === "object" &&
          typeof item.value === "string" &&
          typeof item.label === "string"
      )
    : [];

/**
 * @typedef {{ value: string, label: string }} PublicValueLabel
 */

/**
 * GET `/v1/public/genders` — géneros disponibles (orden de presentación).
 * @returns {Promise<PublicValueLabel[]>}
 */
export const getPublicGenders = async () => {
  const { data } = await axiosClient.get("/v1/public/genders");
  return filterValueLabelItems(data);
};

/**
 * GET `/v1/public/marital-statuses` — estados civiles disponibles (orden de presentación).
 * @returns {Promise<PublicValueLabel[]>}
 */
export const getPublicMaritalStatuses = async () => {
  const { data } = await axiosClient.get("/v1/public/marital-statuses");
  return filterValueLabelItems(data);
};

/**
 * GET `/v1/public/occupations` — listado público de ocupaciones (orden de presentación).
 * @returns {Promise<string[]>}
 */
export const getPublicOccupations = async () => {
  const { data } = await axiosClient.get("/v1/public/occupations");
  return Array.isArray(data) ? data.filter((item) => typeof item === "string") : [];
};

/**
 * @typedef {{ uuid: string, name: string }} PublicSectorOccupation
 */

/**
 * GET `/v1/public/sector-occupations` — listado público de sectores de ocupación (orden de presentación).
 * @returns {Promise<PublicSectorOccupation[]>}
 */
export const getPublicSectorOccupations = async () => {
  const { data } = await axiosClient.get("/v1/public/sector-occupations");
  if (!Array.isArray(data)) return [];
  return data.filter(
    (item) =>
      item &&
      typeof item === "object" &&
      typeof item.uuid === "string" &&
      typeof item.name === "string"
  );
};
